import './DatePickerField.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { uk } from 'date-fns/locale/uk';
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import React from 'react';
import { DateInputField } from './DateInputField';
import {
  formatDateDDMMYYYY,
  isValidDateFormat,
  newFormattedDate,
  parseDate,
} from '~/helpers/utils';
import { SxProps, TextFieldProps, Theme } from '@mui/material';

const DatePickerComponent = ((DatePicker as unknown as { default: ReactNode }).default ??
  DatePicker) as typeof DatePicker;
registerLocale('uk', uk);

export interface IDatePickerField {
  date: string | Date | null;
  onDateChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  minDate?: string | Date | null;
  maxDate?: string | Date | null;
  dataTestId?: string;
  helperText?: string;
  inputRef?: React.Ref<unknown>;
  sx?: SxProps<Theme>;
  variant?: TextFieldProps['variant'];
  isCurrentMaxDate?: boolean;
}

export const DatePickerField = ({
  date,
  onDateChange,
  label,
  placeholder,
  error,
  helperText,
  disabled = false,
  isCurrentMaxDate,
  minDate = null,
  maxDate = null,
  dataTestId,
  inputRef,
  variant,
  sx,
}: IDatePickerField) => {
  const datepickerRef = useRef<DatePicker>(null);

  const [formatedDate, setFormatedDate] = useState(newFormattedDate(date));
  const [dateInputValue, setDateInputValue] = useState(date ? formatDateDDMMYYYY(date) : '');

  const defaultMinDate = new Date(1900, 0, 1);
  const defaultMaxDate = isCurrentMaxDate ? new Date() : new Date(2100, 0, 1);

  const actualMinDate = (minDate || defaultMinDate) as Date;
  const actualMaxDate = (maxDate || defaultMaxDate) as Date;

  useEffect(() => {
    if (date) {
      setFormatedDate(newFormattedDate(date));
      setDateInputValue(
        isValidDateFormat(date as string) ? (date as string) : formatDateDDMMYYYY(date)
      );
    } else {
      setDateInputValue('');
      setFormatedDate(null);
    }
  }, [date]);

  const handleClick = () => {
    if (!disabled && datepickerRef.current) {
      datepickerRef.current?.setOpen(true);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.replace(/\D/g, '');

    if (newValue.length > 2) {
      newValue = `${newValue.slice(0, 2)}.${newValue.slice(2)}`;
    }
    if (newValue.length > 5) {
      newValue = `${newValue.slice(0, 5)}.${newValue.slice(5)}`;
    }
    if (newValue.length === 10) {
      onDateChange(newValue);
      setFormatedDate(parseDate(newValue));
    }

    setDateInputValue(newValue);
    if (!newValue) {
      onDateChange(null);
      setFormatedDate(null);
    }
  };

  const handleInputBlur = () => {
    const parsedDate = newFormattedDate(dateInputValue) as Date;

    if (
      isValidDateFormat(dateInputValue) &&
      parsedDate >= actualMinDate &&
      parsedDate <= actualMaxDate
    ) {
      onDateChange(formatDateDDMMYYYY(parsedDate));
      setFormatedDate(parsedDate);
      setDateInputValue(formatDateDDMMYYYY(parsedDate));
    } else if (!dateInputValue) {
      setFormatedDate(null);
      return;
    } else {
      const actualDate = formatedDate
        ? formatedDate >= actualMinDate
          ? formatedDate <= actualMaxDate
            ? formatedDate
            : actualMaxDate
          : actualMinDate
        : null;

      onDateChange(actualDate ? formatDateDDMMYYYY(actualDate) : null);
      setFormatedDate(actualDate);
      setDateInputValue(actualDate ? formatDateDDMMYYYY(actualDate) : '');
    }
  };

  const handleChangeDate = (date: Date | null) => {
    if (!disabled && date) {
      setFormatedDate(date);
      setDateInputValue(formatDateDDMMYYYY(date));
      onDateChange(formatDateDDMMYYYY(date));
    } else {
      setFormatedDate(null);
      if (dateInputValue) setDateInputValue('');
      onDateChange(null);
    }
  };

  return (
    <DatePickerComponent
      ref={datepickerRef}
      selected={formatedDate}
      onChange={handleChangeDate}
      dateFormat="dd.MM.yyyy"
      placeholderText={placeholder}
      locale={uk}
      maxDate={actualMaxDate}
      minDate={actualMinDate}
      customInput={
        <DateInputField
          disabled={disabled}
          error={error}
          dataTestId={dataTestId}
          helperText={helperText}
          label={label}
          inputRef={inputRef}
          variant={variant}
          sx={sx}
          dateInputValue={dateInputValue}
          onClick={handleClick}
          handleInputBlur={handleInputBlur}
          handleInputChange={handleInputChange}
        />
      }
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      popperPlacement="bottom-start"
      portalId="datepicker-portal-root"
      popperProps={{
        strategy: 'fixed',
      }}
      scrollableYearDropdown
      disabled={disabled}
      popperClassName="react-datepicker-popper"
    />
  );
};
